// Colour Me Mama — deterministic scoring. No AI, no randomness.
// Answer keys are 'A' | 'B' | 'C' | 'D'. Questions are 1-indexed.

export const COLOURS = ["RED", "ORANGE", "YELLOW", "GREEN", "BLUE", "PURPLE", "PINK"];

// SCORING[q][answer] = { COLOUR: points, ... }
export const SCORING = {
  1:  { A: { ORANGE: 4 }, B: { BLUE: 2, GREEN: 1 }, C: { YELLOW: 3 }, D: { PINK: 3 } },
  2:  { A: { ORANGE: 4 }, B: { PINK: 3 }, C: { YELLOW: 2, GREEN: 1 }, D: { PURPLE: 3 } },
  3:  { A: { RED: 2, ORANGE: 1 }, B: { PINK: 2, ORANGE: 2 }, C: { YELLOW: 3 }, D: { GREEN: 3 } },
  4:  { A: { RED: 3 }, B: { PINK: 2, RED: 1 }, C: { BLUE: 3 }, D: { PURPLE: 3 } },
  5:  { A: { PINK: 2, RED: 1 }, B: { YELLOW: 3 }, C: { YELLOW: 3 }, D: { ORANGE: 3 } },
  6:  { A: { PINK: 3 }, B: { PURPLE: 2, GREEN: 1 }, C: { YELLOW: 3 }, D: { BLUE: 3 } },
  7:  { A: { PINK: 3 }, B: { ORANGE: 4 }, C: { PURPLE: 3 }, D: { RED: 3 } },
  8:  { A: { PURPLE: 3 }, B: { GREEN: 2, PURPLE: 1 }, C: { RED: 2, PURPLE: 1 }, D: { PINK: 2, GREEN: 1 } },
  9:  { A: { PINK: 3 }, B: { ORANGE: 3 }, C: { YELLOW: 3 }, D: { PURPLE: 3 } },
  10: { A: { PINK: 3 }, B: { GREEN: 3 }, C: { RED: 3 }, D: { PURPLE: 3 } },
  11: { A: { RED: 2, ORANGE: 1 }, B: { PINK: 3 }, C: { GREEN: 3 }, D: { ORANGE: 3 } },
  12: { A: { PINK: 3 }, B: { ORANGE: 3 }, C: { PURPLE: 3 }, D: { RED: 3 } },
  13: { A: { BLUE: 3 }, B: { RED: 3 }, C: { PURPLE: 3 }, D: { PINK: 2, RED: 1 } },
  14: { A: { BLUE: 3 }, B: { GREEN: 3 }, C: { YELLOW: 3 }, D: { RED: 3 } },
  15: { A: { BLUE: 2, PINK: 1 }, B: { RED: 3 }, C: { PINK: 3 }, D: { PURPLE: 3 } },
  // Q16 — validation. Weighted at 6. Only Red/Pink/Purple/Green.
  16: { A: { RED: 6 }, B: { PINK: 6 }, C: { PURPLE: 6 }, D: { GREEN: 6 } },
};

const Q16_COLOUR = { A: "RED", B: "PINK", C: "PURPLE", D: "GREEN" };
// Only used as a final technical fallback. Never revealed.
const FINAL_FALLBACK_ORDER = ["PINK", "PURPLE", "RED", "ORANGE", "BLUE", "GREEN", "YELLOW"];

/** answers: { 1: 'A'|'B'|'C'|'D', ..., 16: ... } */
export function score(answers) {
  const totals = Object.fromEntries(COLOURS.map((c) => [c, 0]));
  const strongHits = Object.fromEntries(COLOURS.map((c) => [c, 0]));  // >= 3 points on a Q1-15 answer
  const midHits = Object.fromEntries(COLOURS.map((c) => [c, 0]));     // >= 2 points on a Q1-15 answer

  for (let q = 1; q <= 16; q++) {
    const ans = answers[q];
    if (!ans) continue;
    const row = SCORING[q][ans] || {};
    for (const [colour, pts] of Object.entries(row)) {
      totals[colour] += pts;
      if (q <= 15) {
        if (pts >= 3) strongHits[colour] += 1;
        if (pts >= 2) midHits[colour] += 1;
      }
    }
  }

  const q16Colour = Q16_COLOUR[answers[16]] || null;

  // Rank with deterministic tie-breaking.
  const ranked = [...COLOURS].sort((a, b) => {
    if (totals[b] !== totals[a]) return totals[b] - totals[a];
    // Tie-break 1: Q16 colour wins.
    if (q16Colour === a && q16Colour !== b) return -1;
    if (q16Colour === b && q16Colour !== a) return 1;
    // Tie-break 2: more "strong hits" (>=3 pts) across Q1-15.
    if (strongHits[b] !== strongHits[a]) return strongHits[b] - strongHits[a];
    // Tie-break 3: more "mid hits" (>=2 pts) across Q1-15.
    if (midHits[b] !== midHits[a]) return midHits[b] - midHits[a];
    // Tie-break 4: fixed fallback order.
    return FINAL_FALLBACK_ORDER.indexOf(a) - FINAL_FALLBACK_ORDER.indexOf(b);
  });

  return { primary: ranked[0], secondary: ranked[1], totals, ranked };
}
